import { isEqual } from 'lodash-es'
import { TableKey } from '@tradecafe/types/core'
import { createEntityAdapter, EntityState } from '@ngrx/entity'
import { createReducer, createSelector, on } from '@ngrx/store'

import { State } from '../reducers'
import { DeepReadonly } from '@tradecafe/types/utils'
import { ElasticSearchFilters } from 'src/services/elastic-search'
import * as VendorCreditViewActions from './vendor-credit-view.actions'
import { prepareVendorCreditsPayload } from './vendor-credit-view-filters.utils'
import { VendorCreditRow } from 'src/services/data/vendor-credit-elastic.service'

export const vendorCreditViewsFeatureKey = 'vendorCreditViews'

export interface VendorCreditViewsState extends EntityState<VendorCreditRow> {
  filters: Dictionary<ElasticSearchFilters>
  prevQuery: Dictionary<any>
  // vendor credit ids lists grouped by table key
  tables: Dictionary<string[]>
  // total vendor credits lists grouped by table key
  totals: Dictionary<Dictionary<any>>
}

const selectId = row => {
  return row.credit_note_id
}
const adapter = createEntityAdapter<VendorCreditRow>({ selectId })

const initialState: VendorCreditViewsState = adapter.getInitialState({
  tables: {},
  totals: {},
  filters: {},
  prevQuery: {},
})

export const vendorCreditViewReducer = createReducer(
  initialState,
  on(VendorCreditViewActions.loadVendorCreditViews,
    (state, action) => {
      const key = action.tableKey
      const query = prepareVendorCreditsPayload(action.filters, action.page)
      const prev = state.prevQuery[key] || {}

      let { tables, prevQuery } = state
      if (!isEqual(query.query, prev.query)) {
        tables = { ...tables, [key]: undefined } // drop table
        prevQuery = { ...prevQuery, [key]: query } // save query
  
      } else if (!isEqual(query.sort, prev.sort)) {
        const table = [...tables[key] || []]
        tables = { ...tables, [key]: table.fill(undefined) } // clear table
        prevQuery = { ...prevQuery, [key]: query } // save query
      }
      return { ...state, tables, totals: {}, prevQuery }
    }),
  on(VendorCreditViewActions.loadVendorCreditViewsSuccess,
    (state, action) => {
      const key = action.tableKey
      const { total, totals: totals_, vendorCreditViews, payload: { skip } } = action
      const vendorCreditIds = vendorCreditViews.map(selectId)

      let table = Array.from<string>({ length: total }).fill(undefined)
      table.splice(skip, vendorCreditIds.length, ...vendorCreditIds) // NOTE: vendorCreditIds.length or limit?
      const tables = { ...state.tables, [key]: table }
      const totals = { ...state.totals, [key]: totals_ }

      state = adapter.upsertMany(action.vendorCreditViews, state)
      return { ...state, tables, totals }
    }),
  on(VendorCreditViewActions.loadVendorCreditFiltersSuccess,
    (state, action) => {
      const key = action.tableKey
      const filters = { ...state.filters, [key]: action.result }
      return { ...state, filters }
    }),
)
const selectState = (state: State) => state[vendorCreditViewsFeatureKey]
export const { selectEntities: selectVendorCreditViewEntities } = adapter.getSelectors(selectState)

export const selectVendorCredit = (vendorCreditId: string) => createSelector(
  selectVendorCreditViewEntities,
  (vendorCreditViews: Dictionary<VendorCreditRow>) => vendorCreditViews[vendorCreditId]
)

export const selectVendorCreditsByIds = (vendorCreditIds: DeepReadonly<string[]>) => createSelector(
  selectVendorCreditViewEntities,
  (vendorCreditViews: Dictionary<VendorCreditRow>) => vendorCreditIds.map(vendorCreditId => vendorCreditViews[vendorCreditId])
)

const selectTables = createSelector(selectState, state => state.tables)

export const selectVendorCreditsFilters = (key: TableKey) => createSelector(
  selectState,
  state => state.filters[key],
)

export const selectVendorCreditsTotals = (key: TableKey) => createSelector(
  selectState,
  state => state.totals[key],
)

export const selectVendorCreditsTable = (key: TableKey) => createSelector(
  selectVendorCreditViewEntities,
  selectTables,
  (vendorCreditViews, tables) => tables[key] ? tables[key].map(id => vendorCreditViews[id]) : undefined
)
